import { useEffect, useState } from "react";
import { placePayOnDeliveryOrder, type PaymentMethod } from "@/services/payOnDelivery";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShoppingBag, Store, Package, Share2, Loader2 } from "lucide-react";
import { ProductMetaTags } from "@/components/ProductMetaTags";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useWhatsAppShare } from "@/hooks/useWhatsAppShare";
import { BuyNowModal } from "@/components/checkout/BuyNowModal";

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  images?: string[];
  stock: number;
  seller_id: string;
  currency: string;
}

interface SellerInfo {
  business_name: string;
  business_description: string;
  verified: boolean;
  pickup_address_state?: string;
}

export default function ShareProduct() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [seller, setSeller] = useState<SellerInfo | null>(null);
  const [sellerState, setSellerState] = useState('Lagos');
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const { createShareableLink } = useWhatsAppShare();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  // Auto-show modal when coming from WhatsApp with checkout param
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const autoCheckout = urlParams.get("checkout");

    if (autoCheckout === "true" && product && !isGeneratingLink) {
      setTimeout(() => {
        setShowBuyNowModal(true);
      }, 500);
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      // Use secure RPC to avoid exposing seller_id to public users
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_public_product_by_id', {
        p_product_id: productId
      });

      if (rpcError) throw rpcError;
      const productData = rpcData?.[0];
      if (!productData) throw new Error('Product not found');

      // Fetch seller_id from products table (needed for delivery rate calculation)
      let sellerId = '';
      try {
        const { data: prodRow } = await supabase
          .from('products')
          .select('seller_id')
          .eq('id', productData.id)
          .single();
        if (prodRow?.seller_id) {
          sellerId = prodRow.seller_id;
        }
      } catch (e) {
        console.warn('Could not fetch seller_id for product:', e);
      }

      setProduct({
        id: productData.id,
        title: productData.title,
        description: productData.description || '',
        price: productData.price,
        images: productData.images || [],
        stock: productData.stock || 0,
        seller_id: sellerId,
        currency: productData.currency || 'NGN',
      });

      if (productData.business_name) {
        setSeller({
          business_name: productData.business_name,
          business_description: productData.business_description || '',
          verified: productData.seller_verified || false,
          pickup_address_state: productData.seller_pickup_state || undefined,
        });
        if (productData.seller_pickup_state) {
          setSellerState(productData.seller_pickup_state);
        }
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Product not found");
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyNowClick = () => {
    if (!product) return;
    setShowBuyNowModal(true);
  };

  const handleBuyNowConfirm = async (deliveryData: {
    deliveryState: string;
    deliveryCity: string;
    deliveryAddress: string;
    recipientName: string;
    recipientPhone: string;
    deliveryFee: number;
    deliveryType: string;
    buyerEmail?: string;
    paymentMethod?: PaymentMethod;
  }) => {
    if (!product) return;
    setIsGeneratingLink(true);
    if (deliveryData.paymentMethod === 'pay_on_delivery') {
      try {
        const result = await placePayOnDeliveryOrder({
          items: [{ productId: product.id, quantity: 1 }],
          delivery_fee: deliveryData.deliveryFee,
          delivery_type: deliveryData.deliveryType === 'fez' ? 'fez' : 'self',
          recipient_name: deliveryData.recipientName.trim(),
          recipient_phone: deliveryData.recipientPhone.trim(),
          delivery_address_line: deliveryData.deliveryAddress.trim(),
          delivery_city: deliveryData.deliveryCity,
          delivery_state: deliveryData.deliveryState,
          email: deliveryData.buyerEmail,
        });
        setShowBuyNowModal(false);
        window.location.href = `/order-placed?orderId=${result.orderId}&total=${Math.round(result.total)}`;
      } catch (err: any) {
        toast.error(err?.message || "Could not place your order. Please try again.");
      } finally {
        setIsGeneratingLink(false);
      }
      return;
    }


    try {
      const { data, error } = await supabase.functions.invoke("generate-payment-link", {
        body: {
          productId: product.id,
          quantity: 1,
          deliveryFee: deliveryData.deliveryFee,
          deliveryType: deliveryData.deliveryType,
          deliveryAddress: `${deliveryData.deliveryAddress}, ${deliveryData.deliveryCity}, ${deliveryData.deliveryState}`,
          recipientName: deliveryData.recipientName,
          recipientPhone: deliveryData.recipientPhone,
        },
      });

      if (error) throw error;

      if (data?.paymentLink) {
        if (data.orderId) {
          sessionStorage.setItem("pending_order_id", data.orderId);
        }
        if (data.trackingNumber) {
          sessionStorage.setItem("pending_tracking_number", data.trackingNumber);
        }
        if (data.reference) {
          sessionStorage.setItem("pending_reference", data.reference);
        }

        window.location.href = data.paymentLink;
      } else {
        throw new Error("No payment link received");
      }
    } catch (error) {
      console.error("Error generating payment link:", error);
      toast.error("Failed to generate payment link. Please try again.");
    } finally {
      setIsGeneratingLink(false);
      setShowBuyNowModal(false);
    }
  };

  const shareToWhatsApp = async () => {
    if (!product) return;

    const shareableLink = `https://sabitradeng.com/share/product/${product.id}?checkout=true`;

    const excerpt =
      product.description && product.description.length > 100
        ? product.description.substring(0, 100) + "..."
        : product.description || "Check out this amazing product!";

    let statusMessage = `${shareableLink}\n\n`;
    statusMessage += `🛍️ *${product.title}*\n\n`;
    statusMessage += `📝 ${excerpt}\n\n`;
    statusMessage += `💰 *Price: ₦${product.price.toLocaleString()}*\n`;

    if (seller?.business_name) {
      statusMessage += `🏪 *Seller:* ${seller.business_name}\n`;
    }

    statusMessage += `\n✅ Instant checkout\n#SabiTradeNG #OnlineShopping #Nigeria`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: statusMessage,
          url: shareableLink,
        });
        toast.success("Product shared successfully!");
      } else {
        await navigator.clipboard.writeText(statusMessage);
        toast.success("Copied! Paste it into your WhatsApp Status.");
      }
    } catch (error: any) {
      console.error("Error sharing:", error);
      if (error?.name !== "AbortError") {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(statusMessage)}`;
        window.open(whatsappUrl, "_blank");
        toast.success("WhatsApp opened!");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-full max-w-2xl px-4">
          <Breadcrumbs />
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md mx-auto px-4">
          <Breadcrumbs />
          <Card className="max-w-md mx-auto">
            <CardContent className="p-6 text-center">
              <h2 className="text-2xl font-bold mb-2">Product Not Found</h2>
              <p className="text-muted-foreground mb-4">
                This product may have been removed or is no longer available.
              </p>
              <Button onClick={() => navigate("/")}>Go to Homepage</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-4">
      <ProductMetaTags product={product} seller={seller} />
      <div className="max-w-2xl mx-auto">
        <Breadcrumbs />
        <Card className="overflow-hidden shadow-xl">
          {/* Product Image */}
          {product.images?.[0] && (
            <div className="aspect-video relative overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5">
              <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
              <div className="absolute top-4 right-4">
                <Badge variant="secondary" className="backdrop-blur-sm bg-background/80">
                  <Package className="h-3 w-3 mr-1" />
                  {product.stock} in stock
                </Badge>
              </div>
            </div>
          )}

          <CardHeader className="space-y-4">
            {/* Seller Info */}
            {seller && (
              <div className="flex items-center gap-2 text-sm">
                <Store className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{seller.business_name}</span>
                {seller.verified && (
                  <Badge variant="outline" className="text-xs">
                    Verified
                  </Badge>
                )}
              </div>
            )}

            {/* Product Title & Price */}
            <div>
              <CardTitle className="text-2xl mb-2">{product.title}</CardTitle>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-primary">₦{product.price.toLocaleString()}</span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Product Description */}
            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-muted-foreground">{product.description}</p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                className="w-full"
                size="lg"
                variant="gradient"
                onClick={handleBuyNowClick}
                disabled={isGeneratingLink || product.stock === 0}
              >
                {isGeneratingLink ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    Buy Now - ₦{product.price.toLocaleString()}
                  </>
                )}
              </Button>

              <Button className="w-full" size="lg" variant="outline" onClick={shareToWhatsApp}>
                <Share2 className="h-4 w-4 mr-2" />
                Share on WhatsApp
              </Button>
            </div>

            {/* Buy Now Modal - Collects Delivery Info */}
            <BuyNowModal
              open={showBuyNowModal}
              onOpenChange={setShowBuyNowModal}
              product={product}
              sellerState={sellerState}
              onConfirm={handleBuyNowConfirm}
              isProcessing={isGeneratingLink}
            />

            {/* Stock Warning */}
            {product.stock > 0 && product.stock <= 5 && (
              <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <p className="text-sm text-warning font-medium">Only {product.stock} items left in stock!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Secure payment powered by Paystack</p>
          <p className="mt-1">Delivery available nationwide</p>
        </div>
      </div>
    </div>
  );
}